import { FiX } from "react-icons/fi";
import { ExerciseInPlan, Exercise } from "../../../../../types";

interface ExerciseEditorRowProps {
  readonly exercise: ExerciseInPlan;
  readonly exIndex: number;
  readonly exercisesList: Exercise[];
  readonly onUpdate: (
    exIndex: number,
    field: keyof ExerciseInPlan,
    value: string | number,
  ) => void;
  readonly onRemove: (exIndex: number) => void;
  readonly onUpdateWithId?: (exIndex: number, id: number) => void;
}

export function ExerciseEditorRow({
  exercise,
  exIndex,
  exercisesList,
  onUpdate,
  onRemove,
  onUpdateWithId,
}: ExerciseEditorRowProps) {
  // Encontra o exercício correto na lista para garantir que o select mostre o valor certo
  const findExerciseInList = () => {
    if (!exercise) return null;

    // Primeiro tenta encontrar pelo ID
    if (exercise.id) {
      const foundById = exercisesList.find((ex) => ex.id === exercise.id);
      if (foundById) return foundById;
    }

    // Se não encontrou pelo ID, tenta pelo nome (comparação case-insensitive e trim)
    if (exercise.name) {
      const foundByName = exercisesList.find(
        (ex) =>
          ex.name?.trim().toLowerCase() === exercise.name?.trim().toLowerCase(),
      );
      if (foundByName) return foundByName;
    }

    return null;
  };

  const selectedExercise = findExerciseInList();
  let selectValue = "";
  if (selectedExercise?.id) {
    selectValue = String(selectedExercise.id);
  } else if (exercise.id) {
    selectValue = String(exercise.id);
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const exerciseId = Number(e.target.value);
    if (!exerciseId) return;

    const selectedExercise = exercisesList.find((ex) => ex.id === exerciseId);
    if (selectedExercise) {
      onUpdate(exIndex, "name", selectedExercise.name);
      if (selectedExercise.id && onUpdateWithId) {
        onUpdateWithId(exIndex, selectedExercise.id);
      }
    }
  };

  return (
    <div className="rounded-xl border border-purple-200 bg-white p-3 sm:p-4 transition-all hover:shadow-md dark:border-purple-800 dark:bg-gray-900">
      {/* Mobile Layout */}
      <div className="space-y-2 sm:hidden">
        {/* Nome do Exercício */}
        <div>
          <label
            htmlFor={`exercise-name-${exIndex}`}
            className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400"
          >
            Exercício
          </label>
          <select
            id={`exercise-name-${exIndex}`}
            value={selectValue}
            onChange={handleNameChange}
            className="w-full appearance-none rounded-lg border border-stroke bg-gray-50 px-2.5 py-2 text-xs font-medium outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
          >
            <option value="">Selecione um exercício</option>
            {exercisesList.map((ex) => (
              <option key={ex.id} value={String(ex.id)}>
                {ex.name}
              </option>
            ))}
          </select>
        </div>

        {/* Linha 1: Séries e Reps */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label
              htmlFor={`exercise-sets-${exIndex}`}
              className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400"
            >
              Séries
            </label>
            <input
              id={`exercise-sets-${exIndex}`}
              type="number"
              value={exercise.sets || exercise.series || ""}
              onChange={(e) => onUpdate(exIndex, "sets", e.target.value)}
              placeholder="3"
              className="w-full rounded-lg border border-stroke bg-white px-2 py-2 text-center text-xs font-semibold outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
            />
          </div>
          <div>
            <label
              htmlFor={`exercise-reps-${exIndex}`}
              className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400"
            >
              Repetições
            </label>
            <input
              id={`exercise-reps-${exIndex}`}
              type="number"
              value={exercise.reps || exercise.repetitions || ""}
              onChange={(e) => onUpdate(exIndex, "reps", e.target.value)}
              placeholder="12"
              className="w-full rounded-lg border border-stroke bg-white px-2 py-2 text-center text-xs font-semibold outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
            />
          </div>
        </div>

        {/* Linha 2: Carga e Intervalo */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label
              htmlFor={`exercise-load-${exIndex}`}
              className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400"
            >
              Carga
            </label>
            <input
              id={`exercise-load-${exIndex}`}
              type="text"
              value={exercise.load || ""}
              onChange={(e) => onUpdate(exIndex, "load", e.target.value)}
              placeholder="20kg"
              className="w-full rounded-lg border border-stroke bg-white px-2 py-2 text-center text-xs font-semibold outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
            />
          </div>
          <div>
            <label
              htmlFor={`exercise-interval-${exIndex}`}
              className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400"
            >
              Intervalo
            </label>
            <input
              id={`exercise-interval-${exIndex}`}
              type="number"
              value={exercise.interval || ""}
              onChange={(e) => onUpdate(exIndex, "interval", e.target.value)}
              placeholder="60"
              className="w-full rounded-lg border border-stroke bg-white px-2 py-2 text-center text-xs font-semibold outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
            />
          </div>
        </div>

        {/* Botão Remover no mobile */}
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={() => onRemove(exIndex)}
            className="rounded-lg p-1.5 text-red-600 transition-all hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            title="Remover exercício"
          >
            <FiX size={16} />
          </button>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden sm:grid sm:grid-cols-12 sm:gap-3">
        {/* Nome do Exercício */}
        <div className="col-span-12 md:col-span-4">
          <select
            id={`exercise-name-desktop-${exIndex}`}
            value={selectValue}
            onChange={handleNameChange}
            className="w-full appearance-none rounded-lg border border-stroke bg-gray-50 px-3 py-2.5 text-sm font-medium outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
          >
            <option value="">Selecione um exercício</option>
            {exercisesList.map((ex) => (
              <option key={ex.id} value={String(ex.id)}>
                {ex.name}
              </option>
            ))}
          </select>
        </div>

        {/* Séries */}
        <div className="col-span-3 md:col-span-2">
          <input
            type="number"
            value={exercise.sets || exercise.series || ""}
            onChange={(e) => onUpdate(exIndex, "sets", e.target.value)}
            placeholder="3"
            className="w-full rounded-lg border border-stroke bg-white px-3 py-2.5 text-center text-sm font-semibold outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
          />
        </div>

        {/* Reps */}
        <div className="col-span-3 md:col-span-2">
          <input
            type="number"
            value={exercise.reps || exercise.repetitions || ""}
            onChange={(e) => onUpdate(exIndex, "reps", e.target.value)}
            placeholder="12"
            className="w-full rounded-lg border border-stroke bg-white px-3 py-2.5 text-center text-sm font-semibold outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
          />
        </div>

        {/* Carga */}
        <div className="col-span-3 md:col-span-2">
          <input
            type="text"
            value={exercise.load || ""}
            onChange={(e) => onUpdate(exIndex, "load", e.target.value)}
            placeholder="20kg"
            className="w-full rounded-lg border border-stroke bg-white px-3 py-2.5 text-center text-sm font-semibold outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
          />
        </div>

        {/* Intervalo */}
        <div className="col-span-2 md:col-span-1">
          <input
            type="number"
            value={exercise.interval || ""}
            onChange={(e) => onUpdate(exIndex, "interval", e.target.value)}
            placeholder="60"
            className="w-full rounded-lg border border-stroke bg-white px-2 py-2.5 text-center text-sm font-semibold outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
          />
        </div>

        {/* Botão Remover */}
        <div className="col-span-1 flex items-center justify-center md:items-end">
          <button
            type="button"
            onClick={() => onRemove(exIndex)}
            className="rounded-lg p-2 text-red-600 transition-all hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            title="Remover exercício"
          >
            <FiX size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
